import { el, clear } from './dom';
import { chatApi, type ChatMessage } from './game-api';
import { socket, connectSocket } from '../core/network/socket';
import { gicon } from './ui/icons';
import type { GameCtx } from './types';
import { playSfx } from './audio/audio';
import { isPhoneLayout, onLayoutChange } from './mobile/layout';
import { isTouchDevice } from './map/joystick';

const CHAT_SEND = 'chat:send';
const CHAT_MESSAGE = 'chat:message';
const CHAT_BACKLOG = 'chat:backlog';
const MAX_RENDERED = 200;   // keep the DOM light on a busy channel

/**
 * Floating world-chat widget. Persists across tabs (mounted once by the game
 * shell). Loads recent history over REST, streams live messages over the socket,
 * and sends with `chat:send`. Own messages arrive back via the broadcast, so there
 * is no optimistic echo to de-duplicate.
 */
export interface ChatHandle {
  /** Open/close the chat panel. Used by the top-bar chat button on every view. */
  toggle: (open?: boolean) => void;
  /** True while the panel is open. */
  isOpen: () => boolean;
  /** Unread count since the panel was last opened (drives the top-bar badge). */
  onUnread: (cb: (n: number) => void) => void;
  /**
   * Show/hide the whole widget (FAB + panel). The shell hides it outside the Farm
   * view: the FAB is a fixed, high-z bubble in the bottom-left corner, and on the
   * Shop/Arena/Quests/Awards/Admin pages it floated on top of their content
   * instead of over the game world it belongs to.
   */
  setVisible: (visible: boolean) => void;
  teardown: () => void;
}

export function mountChat(host: HTMLElement, ctx: GameCtx): ChatHandle {
  const meId = ctx.user.id;
  let open = false;
  let unread = 0;
  const seen = new Set<string>();

  // ─── DOM ────────────────────────────────────────────────────────────────────
  const list = el('div', 'flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-1.5 text-[13px]');
  const input = el('input',
    'flex-1 bg-surface-2 border border-border rounded px-2.5 py-1.5 text-sm outline-none focus:border-gold',
    { type: 'text', maxlength: '500', placeholder: 'Say something…' }) as HTMLInputElement;
  const sendBtn = el('button',
    'px-3 py-1.5 rounded bg-gold text-bg font-bold text-sm hover:brightness-105 active:scale-95 transition',
    {}, 'Send') as HTMLButtonElement;

  const panel = el('div',
    // Sized down on a phone: a 320×400 panel over a landscape phone screen covers
    // most of the farm and sits on top of the joystick. Below `sm` it becomes a
    // short, narrow strip anchored in the corner; from `sm` up it is the full panel.
    'flex flex-col w-[240px] sm:w-[320px] max-w-[80vw] h-[210px] sm:h-[400px] max-h-[46vh] sm:max-h-[70vh] '
    + 'bg-surface border border-border rounded-xl shadow-lift overflow-hidden text-[13px] sm:text-sm',
    {},
    el('div', 'flex items-center gap-2 px-3 py-2 bg-surface-2 border-b border-border', {},
      gicon('chat', 16),
      el('div', 'font-heading text-gold text-sm flex-1', {}, 'World Chat'),
      el('button', 'tb-close-x text-text-dim hover:text-text text-lg leading-none', { onClick: () => toggle(false) }, '×')),
    list,
    el('div', 'flex gap-2 p-2 border-t border-border bg-surface-2', {}, input, sendBtn));

  const badge = el('span',
    'absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-error text-white text-[11px] font-mono grid place-items-center hidden');
  const fab = el('button',
    'relative grid place-items-center w-12 h-12 rounded-full bg-gold text-bg shadow-lift hover:brightness-105 active:scale-95 transition',
    { title: 'World chat' }, gicon('chat', 22), badge);

  // Bottom-LEFT, sitting just above the "WASD / click to move" hint so it never
  // covers the zoom controls (which live bottom-right).
  const wrap = el('div', 'fixed bottom-9 left-3 z-[55] flex flex-col items-start gap-2', { id: 'tb-chat' }, panel, fab);
  panel.classList.add('hidden');
  host.appendChild(wrap);

  /**
   * Phone: the bubble grew UPWARD out of the bottom-left corner, i.e. straight over
   * the thumbstick — opening chat cost you the ability to walk. On a phone-shaped
   * viewport it becomes a right-edge drawer instead (same geometry as the bag) and
   * the floating FAB is dropped: the header/immersive chat buttons already open it,
   * so a third trigger was only ever something else to collide with.
   */
  const applyChatLayout = () => {
    // `|| isTouchDevice()`: a landscape TABLET (1440x900) is not a phone layout,
    // but it still gets the thumbstick — and the desktop bubble grows out of the
    // same bottom-left corner the stick occupies (measured: 2304px2 of overlap).
    const phone = isPhoneLayout() || isTouchDevice();
    fab.style.display = phone ? 'none' : '';
    wrap.className = phone
      ? 'fixed right-0 top-0 bottom-0 z-[55] flex flex-col items-stretch'
      : 'fixed bottom-9 left-3 z-[55] flex flex-col items-start gap-2';
    panel.classList.toggle('tb-chat-drawer', phone);
  };
  applyChatLayout();
  // Kept so the phone/desktop swap follows a rotation or resize. The unsubscribe is
  // held for the widget's teardown; the chat widget is mounted once per session by
  // the game shell and lives as long as the page, so it is not disposed here.
  void onLayoutChange(applyChatLayout);

  // ─── Rendering ────────────────────────────────────────────────────────────────
  function atBottom(): boolean {
    return list.scrollHeight - list.scrollTop - list.clientHeight < 40;
  }
  function scrollToEnd() { list.scrollTop = list.scrollHeight; }

  function addMessage(m: ChatMessage, stick: boolean) {
    if (seen.has(m.id)) return;
    seen.add(m.id);
    const mine = m.senderId === meId;
    const time = new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const row = el('div', mine ? 'text-right' : 'text-left', {},
      el('div', 'text-[10px] text-text-dim font-mono', {}, `${mine ? 'You' : (m.senderUsername ?? 'Player')} · ${time}`),
      el('div',
        `inline-block px-2.5 py-1 rounded-lg max-w-[85%] break-words ${mine ? 'bg-gold/20 text-text' : 'bg-surface-2 text-text'}`,
        {}, m.content));
    list.appendChild(row);
    while (list.childElementCount > MAX_RENDERED) list.firstElementChild?.remove();
    if (stick) scrollToEnd();
  }

  // ─── Open / close ─────────────────────────────────────────────────────────────
  let unreadCb: ((n: number) => void) | null = null;
  function setUnread(n: number) {
    unread = n;
    badge.textContent = n > 99 ? '99+' : String(n);
    badge.classList.toggle('hidden', n === 0);
    unreadCb?.(n);
  }

  function toggle(next = !open) {
    open = next;
    panel.classList.toggle('hidden', !open);
    if (open) {
      setUnread(0);
      scrollToEnd();
      input.focus();
    }
  }
  fab.addEventListener('click', () => toggle());

  // Escape closes it, like every other overlay in the game. On a phone the chat is
  // a right-edge drawer whose ONLY other exit is an 11px-wide `×` glyph, and the
  // drawer covers the header (i.e. the button that opened it) — so without this the
  // panel was, in practice, undismissable. Capture phase, and NOT swallowed when the
  // player is mid-message: Escape from the focused input closes it too (the field is
  // the most likely place their focus is).
  const onEsc = (ev: KeyboardEvent) => {
    if (ev.key !== 'Escape' || !open) return;
    ev.stopPropagation();
    toggle(false);
  };
  document.addEventListener('keydown', onEsc, true);

  // ─── Send ─────────────────────────────────────────────────────────────────────
  const SEND_ACK_MS = 6000;   // beyond this the message is treated as never sent

  function sendFailed(content: string, message: string) {
    // Put the text BACK. Send used to clear the field the instant it was clicked and
    // only restore it inside the ack — so with the socket down (or simply slow) a
    // typed message vanished with no error and no way to recover what you wrote.
    if (!input.value) input.value = content;
    const err = el('div', 'text-center text-[11px] text-error', {}, message);
    list.appendChild(err); scrollToEnd();
    setTimeout(() => err.remove(), 4000);
  }

  function doSend() {
    const content = input.value.trim();
    if (!content) return;
    if (!socket.connected) {
      // Nothing will ack an emit on a dead socket, so don't clear the field at all.
      sendFailed(content, 'Not connected — message not sent');
      connectSocket();
      return;
    }
    input.value = '';
    let settled = false;
    const timeout = window.setTimeout(() => {
      if (settled) return; settled = true;
      sendFailed(content, 'No response from the server — message not sent');
    }, SEND_ACK_MS);
    socket.emit(CHAT_SEND, { content }, (res?: { error?: string }) => {
      if (settled) return; settled = true;
      window.clearTimeout(timeout);
      if (res?.error) sendFailed(content, res.error);
    });
    input.focus();
  }
  sendBtn.addEventListener('click', doSend);
  // Keep typing in chat from leaking to the game's window key listeners (WASD would
  // walk the hero, p/h toggle arena debug). stopPropagation contains it to the input.
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); doSend(); }
  });

  // ─── Live stream ────────────────────────────────────────────────────────────────
  const onMessage = (m: ChatMessage) => {
    const stick = atBottom() || open;
    addMessage(m, stick);
    // Somebody else said something — a quiet blip, gated so a burst of chatter
    // can't machine-gun it (see the cue gate in audio/limiter.ts).
    if (m.senderId !== meId) playSfx('chat_message');
    if (!open && m.senderId !== meId) setUnread(unread + 1);
  };
  socket.on(CHAT_MESSAGE, onMessage);
  // On connect the server replays the recent conversation (bot + player) so the
  // room is populated immediately, not only once a fresh live line arrives. These
  // dedupe against history + live via `seen`, and against the unread badge (they're
  // backlog, not new arrivals).
  const onBacklog = (msgs: ChatMessage[]) => {
    if (!Array.isArray(msgs)) return;
    const stick = atBottom() || open;
    for (const m of msgs) addMessage(m, false);
    if (stick) scrollToEnd();
  };
  socket.on(CHAT_BACKLOG, onBacklog);
  // Tell the server we're here so the bot room greets us / hands us a discussion.
  const sayHello = () => socket.emit('chat:join');
  socket.on('connect', sayHello);
  connectSocket();
  if (socket.connected) sayHello();

  // ─── History (initial) ──────────────────────────────────────────────────────────
  let disposed = false;
  chatApi.history(50)
    .then((msgs) => { if (disposed) return; for (const m of msgs) addMessage(m, false); scrollToEnd(); })
    .catch(() => { if (!disposed) list.appendChild(el('div', 'text-center text-[11px] text-text-dim', {}, 'Chat unavailable')); });

  // ─── Teardown ──────────────────────────────────────────────────────────────────
  const teardown = () => {
    disposed = true;
    socket.off(CHAT_MESSAGE, onMessage);
    socket.off(CHAT_BACKLOG, onBacklog);
    socket.off('connect', sayHello);
    document.removeEventListener('keydown', onEsc, true);
    if (host.contains(wrap)) host.removeChild(wrap);
    clear(list);
  };

  return {
    toggle: (next?: boolean) => toggle(next ?? !open),
    isOpen: () => open,
    onUnread: (cb) => { unreadCb = cb; cb(unread); },
    setVisible: (visible: boolean) => {
      wrap.style.display = visible ? 'flex' : 'none';
      if (!visible && open) toggle(false);
    },
    teardown,
  };
}
